import {generateTokensFromObject} from '../__utils';
import {space} from '../space';

import {
  baseTypography,
  fontFamilyTokens,
  fontWeightTokens,
  remify,
} from './_shared';
import type {Sizing} from './_shared';

/**
 * Values derived from Spring Foundations figma
 *
 * @See https://www.figma.com/design/kcg95u086DD7Sa9or8Bwpq/%F0%9F%8C%B1--Foundations---Spring-2.0?node-id=10933-3576&t=onU5m7tJSA9Y9NHT-4
 */
export const typography: Sizing['typography'] = {
  ...baseTypography,
  fontFamily: {
    ...baseTypography.fontFamily,
    // System styles
    title: fontFamilyTokens.sansSerif,
    heading: fontFamilyTokens.sansSerif,
    body: fontFamilyTokens.sansSerif,
    helpText: fontFamilyTokens.sansSerif,
    code: fontFamilyTokens.mono,
    // Branded styles
    brandedDisplay1: fontFamilyTokens.branded,
    brandedDisplay2: fontFamilyTokens.branded,
    brandedDisplay3: fontFamilyTokens.branded,
    brandedDisplay4: fontFamilyTokens.branded,
    brandedDisplay5: fontFamilyTokens.branded,
    brandedDisplay6: fontFamilyTokens.branded,
    brandedDisplaySubtitle: fontFamilyTokens.branded,
    brandedHeading: fontFamilyTokens.branded,
    brandedBody: fontFamilyTokens.branded,
    brandedHelpText: fontFamilyTokens.branded,
  },
  fontWeight: {
    ...baseTypography.fontWeight,
    // System styles
    title: fontWeightTokens.semibold,
    heading: fontWeightTokens.semibold,
    body: fontWeightTokens.normal,
    helpText: fontWeightTokens.normal,
    code: fontWeightTokens.normal,
    // Branded styles
    brandedDisplay1: fontWeightTokens.semibold,
    brandedDisplay2: fontWeightTokens.semibold,
    brandedDisplay3: fontWeightTokens.semibold,
    brandedDisplay4: fontWeightTokens.semibold,
    brandedDisplay5: fontWeightTokens.semibold,
    brandedDisplay6: fontWeightTokens.semibold,
    brandedDisplaySubtitle: fontWeightTokens.normal,
    brandedHeading: fontWeightTokens.semibold,
    brandedBody: fontWeightTokens.normal,
    brandedHelpText: fontWeightTokens.normal,
  },
  fontSize: {
    // System styles
    title: remify(15),
    heading: remify(13),
    body: remify(13),
    helpText: remify(12),
    code: remify(13),
    // Branded typography styles
    brandedDisplay1: `clamp(${remify(32)}, 6vw, ${remify(84)})`,
    brandedDisplay2: `clamp(${remify(32)}, 6vw, ${remify(60)})`,
    brandedDisplay3: `clamp(${remify(24)}, 5vw, ${remify(32)})`,
    brandedDisplay4: `clamp(${remify(18)}, 4vw, ${remify(24)})`,
    brandedDisplay5: remify(18),
    brandedDisplay6: remify(16),
    brandedDisplaySubtitle: remify(18),
    brandedHeading: remify(16),
    brandedBody: remify(14),
    brandedHelpText: remify(12),
  },
  lineHeight: {
    // System styles
    title: remify(20),
    heading: remify(18),
    body: remify(18),
    helpText: '1rem',
    code: remify(18),
    // Branded styles
    brandedDisplay1: '96%',
    brandedDisplay2: '104%',
    brandedDisplay3: '104%',
    brandedDisplay4: '130%',
    brandedDisplay5: '130%',
    brandedDisplay6: '130%',
    brandedDisplaySubtitle: '160%',
    brandedHeading: '160%',
    brandedBody: remify(24),
    brandedHelpText: remify(16),
  },
} as const;

export const ui: Sizing['ui'] = {
  accordion: {
    adornment: {
      rightPosition: '32px',
    },
    button: {
      padding: {
        comfortable: `${space[3]} ${space[2]} ${space[3]} ${space[3]}`,
        compact: `${space[2]} ${space[2]} ${space[2]} ${space[3]}`,
      },
    },
  },
  activeCursor: 'pointer',
  autocomplete: {
    clearButton: {
      right: space[8], // 32px (dropdown trigger width)
    },
    control: {
      padding: `calc(${space[2]} - 2px) calc(${space[2]} - 1px)`,
    },
    input: {
      height: space[5],
      padding: '6px',
    },
    label: {
      virtualized: {
        padding: `${space[2]} ${space[3]} ${space[1]}`,
      },
    },
    listBox: {
      padding: `${space[2]} 0px`,
    },
    option: {
      minHeight: space[5],
      padding: `${space[1]} ${space[3]}`,
    },
  },
  avatar: {
    xSmall: space[5],
    small: space[6],
    medium: space[7],
    large: space[9],
    xLarge: space[16],
  },
  avatarGroup: {
    spacing: {
      xSmall: `-${space[1]}`,
      small: `-${space[1]}`,
      medium: `-${space[1]}`,
      large: `-${space[2]}`,
      xLarge: `-${space[3]}`,
    },
    rowItem: {
      margin: space[3],
      padding: `6px ${space[2]} 6px ${space[3]}`,
    },
  },
  button: {
    padding: {
      comfortable: `calc(${space[2]} - 1px) ${space[3]}`,
      compact: `calc(${space[2]} - 1px)`,
    },
  },
  checkbox: {
    boxSize: '16px !important', // !important seems necessary for `cms-csv-ref-new-fields.spec.ts` to pass
    checkSize: '16px !important', // !important seems necessary for `cms-csv-ref-new-fields.spec.ts` to pass
  },
  dataTable: {
    filterPopover: {
      filterPopoverSearchPadding: space[3],
      filterPopoverContentPadding: `${space[3]} ${space[2]} ${space[3]} ${space[2]}`,
      filterPopoverSearchEmptyStatePadding: `${space[4]} ${space[0]}`,
    },
    bulkActionsBar: {
      containerPadding: `${space[3]} ${space[0]}`,
      labelPadding: `${space[0]} ${space[5]}`,
      actionsPadding: `${space[0]} ${space[5]} ${space[0]} ${space[0]}`,
      dismissSectionPadding: `${space[0]} ${space[2]} ${space[0]} ${space[3]}`,
      dividerHeight: space[6],
    },
  },
  dialog: {
    header: {
      padding: space[3],
    },
  },
  emptyState: {
    padding: {
      top: space[3],
      right: space[4],
      bottom: space[4],
      left: space[4],
    },
    margin: {
      button: {
        top: space[1],
        bottom: space[0],
      },
      link: {
        top: space[2],
        bottom: space[2],
      },
    },
  },
  iconButton: {
    padding: {
      comfortable: space[2],
      compact: space[0],
    },
  },
  input: {
    padding: `calc(${space[2]} - 2px)`, // account for border & line-height
  },
  menu: {
    label: {
      padding: `${space[1]} ${space[3]}`,
    },
    item: {
      prefix: {
        height: '18px', // should match $body lineHeight
      },
      nestedPadding: `${space[1]} ${space[2]} ${space[1]} ${space[3]}`,
      padding: `${space[1]} ${space[3]}`,
    },
    content: {
      padding: `${space[2]} 0px`,
    },
    divider: {
      margin: `${space[2]} 0px`,
    },
  },
  modal: {
    header: {
      padding: '6px', // needed to match IconButton height of 32px: 20px font line height + 6px top padding + 6px bottom padding
    },
  },
  navbar: {
    height: {
      dashboard: '58px',
      marketplace: space[16],
    },
  },
  numberInput: {
    input: {
      padding: `${space[1]} ${space[2]}`,
      height: space[8],
      withPrefixOrSuffix: {
        paddingX: space[2],
        minWidth: space[8],
      },
    },
    controls: {
      width: space[5],
      height: 'auto',
    },
    stepper: {
      input: {
        width: space[8],
      },
      button: {
        width: space[8],
        height: space[8],
      },
    },
  },
  popover: {
    footer: {
      padding: space[3],
      spacing: space[3],
    },
    header: {
      iconButtonHeight: space[8],
      padding: space[2],
      spacing: space[2],
    },
    headerContent: {
      padding: `0px ${space[1]}`,
    },
  },
  rowItem: {
    checkbox: {
      marginTop: `calc(${space[1]} - 1px)`,
    },
    prefix: {
      marginTop: space[0.5],
    },
    dragHandle: {
      checkboxHandleWidth: `calc(${space[3]} - 1px)`,
      paddingLeft: space[1],
    },
    padding: {
      comfortable: {
        vertical: space[2],
        right: space[3],
        left: space[3],
      },
      compact: {
        vertical: space[1],
        right: space[2],
        left: space[3],
      },
    },
    spacing: space[3],
    content: {
      spacing: space[2],
    },
  },
  segmentedControl: {
    /**
     * This is the SegmentedControlButton height.
     * Derived from the desired height of the SegmentedControl (32px) minus padding (top: 1px, bottom: 1px)
     */
    height: '30px !important',
  },
  select: {
    control: {
      buttonPadding: `calc(${space[2]} - 1px) ${space[2]}`,
      chevronRightPadding: `calc(${space[2]} - 2px)`,
      padding: `calc(${space[2]} - 2px) calc(${space[2]} - 1px)`,
    },
    content: {
      padding: space[2],
    },
    option: {
      padding: `${space[1]} ${space[3]}`,
      spacing: space[2],
    },
    groupHeader: {
      padding: `${space[1]} ${space[3]}`,
    },
  },
  switch: {
    track: {
      comfortable: {
        height: space[5],
        width: space[8],
      },
      compact: {
        height: space[4],
        width: space[6],
      },
    },
    knob: {
      comfortable: {
        height: space[4],
        width: space[4],
      },
      compact: {
        height: space[3],
        width: space[3],
      },
    },
  },
  table: {
    emptyState: {
      paddingTop: space[5],
      paddingRight: space[4],
      paddingBottom: space[5],
      paddingLeft: space[4],
    },
    padding: {
      comfortable: `${space[3]} ${space[5]}`,
      compact: `${space[2]} ${space[4]}`,
    },
  },
  tabs: {
    tab: {
      height: '50px',
      margin: space[0.5],
      button: {
        comfortable: {
          padding: {
            horizontal: space[3],
            vertical: space[2],
            column: space[3],
          },
        },
        compact: {
          padding: {
            horizontal: space[3],
            vertical: space[2],
            column: space[3],
          },
        },
      },
      underline: {
        comfortable: {
          padding: {
            horizontal: space[0],
            vertical: space[5],
            column: space[1],
          },
        },
        compact: {
          padding: {
            horizontal: space[0],
            vertical: space[5],
            column: space[1],
          },
        },
      },
    },
    tabList: {
      spacing: {
        underline: {
          row: space[5],
          column: space[4],
        },
        button: {
          row: space[1],
          column: space[4],
        },
      },
    },
    tabPanel: {
      padding: `${space[8]} 0px`,
    },
  },
  timeline: {
    header: {
      padding: `${space[3]} ${space[4]}`,
    },
    body: {
      padding: `${space[6]} ${space[4]}`,
    },
  },
  toast: {
    body: {
      gap: `${space[2]}`,
    },
  },
  tooltip: {
    padding: {
      comfortable: space[3],
      compact: `${space[1]} ${space[2]}`,
      commentBubble: `${space[4]} ${space[3]} ${space[5]} ${space[3]}`,
    },
  },
} as const;

const sizingValues = {
  typography,
  ui,
};

export const {setTokens: xLargeVars} = generateTokensFromObject(sizingValues, {
  applyNamespace: true,
});
